import { useEffect } from 'react';
import { useStore } from 'react-redux';
import { toast } from 'react-toastify';
import LyftSocketService from '@provider/LyftSocketService';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import type { RootState } from '@store/store';
import { sessionSetter } from '@utils/storage';
import { selectUsers } from '@store/slices/users/usersSlice';
import { X_HEADER_ID_NAME } from '@src/utils/constants';
import {
  CONVERSATION_FILTERS,
  driverChatApi,
  patchConversations,
  useMarkDriverChatReadMutation,
} from '@store/rtk/driverChat';
import {
  openPanelConversation,
  selectVisibleConversationIds,
  setDriverChatConnection,
} from '@store/slices/driverChat/driverChatSlice';
import {
  DRIVER_CHAT_TOAST_TITLE,
  DRIVER_CHAT_TOPIC_PREFIX,
  driverChatConversationTopic,
  driverChatPayorTopic,
} from '@component/DriverChat/constants';
import { toSocketEvent } from '@component/DriverChat/adapters';
import {
  ChatMessage,
  DeliveryState,
  DriverChatSocketEvent,
} from '@component/DriverChat/types';

const DELIVERY_RANK: Record<DeliveryState, number> = {
  failed: 0,
  sending: 1,
  sent: 2,
  delivered: 3,
  read: 4,
};

// Same identity the REST calls send as X-Payor-Id; falls back to the logged-in
// user's payor in case the header is written a moment after login.
const getPayorId = (associated?: number | string | null) =>
  sessionSetter.getItem<Record<string, any>>(X_HEADER_ID_NAME)?.payor ??
  associated ??
  null;

/**
 * Bridges the STOMP chat topics into the RTK Query cache. Mounted once by
 * DriverChatLauncher.
 *
 *  - /topic/driver-chat/payor/{payorId}        always: toasts, list and badge refresh
 *  - /topic/driver-chat/conversation/{id}      only while that thread is on screen
 *
 * Chat payloads can contain PHI: nothing here is logged.
 */
export const useDriverChatSocket = (enabled: boolean) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [markRead] = useMarkDriverChatReadMutation();
  const visibleKey = useAppSelector((state) =>
    selectVisibleConversationIds(state).join(',')
  );

  const { userInfo } = useAppSelector(selectUsers);
  const payorId = getPayorId((userInfo as any)?.associated);
  // Payor topic: lives as long as the launcher is enabled.
  useEffect(() => {
    if (!enabled || !payorId) return undefined;
    // Make sure this session carries a chat ticket before subscribing.
    LyftSocketService.ensureChatTicket();
    return LyftSocketService.subscribe(driverChatPayorTopic(payorId));
  }, [enabled, payorId]);

  // Conversation topics: one per thread currently on screen.
  useEffect(() => {
    if (!enabled || !visibleKey) return undefined;
    const unsubscribers = visibleKey
      .split(',')
      .map((id) =>
        LyftSocketService.subscribe(driverChatConversationTopic(Number(id)))
      );
    return () => unsubscribers.forEach((off) => off());
  }, [enabled, visibleKey]);

  useEffect(() => {
    if (!enabled) return undefined;

    let hasConnected = LyftSocketService.isConnected();
    dispatch(
      setDriverChatConnection(hasConnected ? 'connected' : 'connecting')
    );

    const refreshLists = () =>
      dispatch(driverChatApi.util.invalidateTags(['Conversations', 'Unread']));

    const upsertMessage = (message: ChatMessage) => {
      dispatch(
        driverChatApi.util.updateQueryData(
          'getDriverChatMessages',
          message.conversationId,
          (draft) => {
            const existing = draft.messages.find(
              (m) =>
                m.id === message.id ||
                (!!message.clientMessageId &&
                  m.clientMessageId === message.clientMessageId)
            );
            if (existing) Object.assign(existing, message);
            else draft.messages.push(message);
          }
        )
      );
    };

    const isVisible = (conversationId: number | null) =>
      conversationId != null &&
      selectVisibleConversationIds(store.getState()).includes(conversationId);

    const handleMessage = (message: ChatMessage) => {
      const { conversationId } = message;
      upsertMessage(message);

      let isKnownConversation = false;
      patchConversations(dispatch, (draft) => {
        const conversation = draft.find(
          (c) => c.conversationId === conversationId
        );
        if (!conversation) return;
        isKnownConversation = true;
        if (message.sender !== 'SYSTEM') {
          conversation.lastMessage = {
            sender: message.sender,
            text: message.text,
            createdAt: message.createdAt,
            status: message.status,
          };
        }
      });
      if (!isKnownConversation) refreshLists();

      // The thread is open and the dispatcher is looking at it: it counts as read.
      if (message.sender === 'DRIVER' && isVisible(conversationId)) {
        markRead({ conversationId, lastReadMessageId: Number(message.id) });
      }
    };

    const handleDelivery = (
      conversationId: number,
      messageId: string,
      status: DeliveryState
    ) => {
      dispatch(
        driverChatApi.util.updateQueryData(
          'getDriverChatMessages',
          conversationId,
          (draft) => {
            const message = draft.messages.find((m) => m.id === messageId);
            if (!message) return;
            const current = message.status ?? 'sent';
            // Receipts only move forward; a late SENT must not undo DELIVERED.
            if (DELIVERY_RANK[status] > DELIVERY_RANK[current]) {
              message.status = status;
            }
          }
        )
      );
    };

    const handleEvent = (event: DriverChatSocketEvent) => {
      switch (event.type) {
        case 'message':
          handleMessage(event.message);
          break;
        case 'delivery':
          handleDelivery(event.conversationId, event.messageId, event.status);
          break;
        case 'read':
          // Own read receipt moved (possibly from another tab): refresh the badge.
          dispatch(driverChatApi.util.invalidateTags(['Unread']));
          break;
        case 'notification': {
          // "New message from driver · Segment #48213": the segment number is not
          // message content; the text itself is never in the toast. Read it before
          // refreshing, which drops the cached list.
          const segmentId =
            event.segmentId ??
            CONVERSATION_FILTERS.flatMap(
              (filter) =>
                driverChatApi.endpoints.getDriverConversations.select({
                  filter,
                })(store.getState()).data ?? []
            ).find((c) => c.conversationId === event.conversationId)?.segmentId;

          const isOpen = isVisible(event.conversationId);
          // Show it in the float chat right away (badge and row), then let the
          // server's numbers replace this once the refresh below lands.
          if (!isOpen && event.conversationId != null) {
            patchConversations(dispatch, (draft) => {
              const conversation = draft.find(
                (c) => c.conversationId === event.conversationId
              );
              if (conversation) conversation.unreadCount += 1;
            });
            dispatch(
              driverChatApi.util.updateQueryData(
                'getDriverChatUnreadCount',
                undefined,
                (count) => count + 1
              )
            );
          }
          refreshLists();
          // A message landed in a thread that is not open: mark its history stale
          // so reopening shows it (the open thread gets it over its own topic).
          if (event.conversationId != null && !isOpen) {
            dispatch(
              driverChatApi.util.invalidateTags([
                { type: 'Messages', id: event.conversationId },
              ])
            );
          }
          // The open thread already shows the message; only toast for hidden ones.
          if (!isOpen) {
            toast.info(
              segmentId
                ? `${DRIVER_CHAT_TOAST_TITLE} · Segment #${segmentId}`
                : DRIVER_CHAT_TOAST_TITLE,
              {
                toastId: `driver-chat-${event.conversationId ?? 'any'}`,
                onClick: () =>
                  dispatch(openPanelConversation(event.conversationId)),
              }
            );
          }
          break;
        }
        case 'rideStatus':
          if (event.message) upsertMessage(event.message);
          // Conversation status (ACTIVE/COMPLETED) may have changed too.
          refreshLists();
          break;
        default:
          break;
      }
    };

    const offMessage = LyftSocketService.onMessage((payload, channel) => {
      if (!channel.startsWith(DRIVER_CHAT_TOPIC_PREFIX)) return;
      const event = toSocketEvent(payload);
      if (event) handleEvent(event);
    });

    const offConnection = LyftSocketService.onConnectionChange((connected) => {
      if (connected) {
        // Resync anything missed while the socket was down.
        if (hasConnected) {
          dispatch(
            driverChatApi.util.invalidateTags([
              'Conversations',
              'Messages',
              'Unread',
            ])
          );
        }
        hasConnected = true;
        dispatch(setDriverChatConnection('connected'));
      } else if (hasConnected) {
        dispatch(setDriverChatConnection('reconnecting'));
      }
    });

    return () => {
      offMessage();
      offConnection();
    };
  }, [enabled]);
};
